import {
  PLAY_JOIN_POLICIES,
  type PlayGenderRestriction,
  type PlayJoinPolicy,
  type PlayParticipantGender,
  type PlayParticipantType,
} from '../types/playEvent';

export interface PlayJoinPolicyModuleAccess {
  hasClubManagement: boolean;
  hasAcademyManagement: boolean;
}

/** Whether a join policy is selectable for the org's active product modules. */
export function isPlayJoinPolicyAvailableForModules(
  joinPolicy: PlayJoinPolicy,
  access: PlayJoinPolicyModuleAccess
): boolean {
  switch (joinPolicy) {
    case 'open':
      return true;
    case 'members_only':
      return access.hasClubManagement;
    case 'students_only':
      return access.hasAcademyManagement;
    case 'members_and_students':
      return access.hasClubManagement && access.hasAcademyManagement;
    default:
      return false;
  }
}

/** Join policies an operator may choose given club/academy module entitlements. */
export function availablePlayJoinPolicies(
  access: PlayJoinPolicyModuleAccess
): PlayJoinPolicy[] {
  return PLAY_JOIN_POLICIES.filter((policy) =>
    isPlayJoinPolicyAvailableForModules(policy, access)
  );
}

/** Options for join-policy selects, preserving an existing saved policy when modules change. */
export function playJoinPoliciesForSelect(
  access: PlayJoinPolicyModuleAccess,
  currentPolicy?: PlayJoinPolicy
): PlayJoinPolicy[] {
  const policies = availablePlayJoinPolicies(access);
  if (currentPolicy && !policies.includes(currentPolicy)) {
    return [...policies, currentPolicy];
  }
  return policies;
}

export interface PlayJoinPolicyInput {
  joinPolicy: PlayJoinPolicy;
  participantType: PlayParticipantType;
  hasActiveMembership?: boolean;
}

export type PlayJoinPolicyErrorCode =
  | 'GUEST_NOT_ALLOWED'
  | 'MEMBERS_ONLY'
  | 'STUDENTS_ONLY'
  | 'ACTIVE_MEMBERSHIP_REQUIRED';

/** Participant types that may join (and need pricing) for a join policy. */
export function participantTypesForJoinPolicy(
  joinPolicy: PlayJoinPolicy
): PlayParticipantType[] {
  switch (joinPolicy) {
    case 'members_only':
      return ['member'];
    case 'students_only':
      return ['student'];
    case 'members_and_students':
      return ['member', 'student'];
    default:
      return ['member', 'student', 'guest'];
  }
}

/** Pricing fields to show for a join policy, scoped to active org product modules. */
export function pricingParticipantTypesForJoinPolicy(
  joinPolicy: PlayJoinPolicy,
  access: PlayJoinPolicyModuleAccess
): PlayParticipantType[] {
  return participantTypesForJoinPolicy(joinPolicy).filter((type) => {
    if (type === 'member') return access.hasClubManagement;
    if (type === 'student') return access.hasAcademyManagement;
    return true;
  });
}

export function validatePlayJoinPolicy(
  input: PlayJoinPolicyInput
): { ok: true } | { ok: false; code: PlayJoinPolicyErrorCode; message: string } {
  const { joinPolicy, participantType, hasActiveMembership } = input;

  if (joinPolicy === 'open') {
    return { ok: true };
  }

  if (joinPolicy === 'members_only') {
    if (participantType !== 'member') {
      return {
        ok: false,
        code: 'MEMBERS_ONLY',
        message: 'This event is for members only',
      };
    }
    if (!hasActiveMembership) {
      return {
        ok: false,
        code: 'ACTIVE_MEMBERSHIP_REQUIRED',
        message: 'Active membership is required to join this event',
      };
    }
    return { ok: true };
  }

  if (joinPolicy === 'students_only') {
    if (participantType !== 'student') {
      return {
        ok: false,
        code: 'STUDENTS_ONLY',
        message: 'This event is for students only',
      };
    }
    return { ok: true };
  }

  if (joinPolicy === 'members_and_students') {
    if (participantType === 'guest') {
      return {
        ok: false,
        code: 'GUEST_NOT_ALLOWED',
        message: 'Guests cannot join this event',
      };
    }
    if (participantType === 'member' && !hasActiveMembership) {
      return {
        ok: false,
        code: 'ACTIVE_MEMBERSHIP_REQUIRED',
        message: 'Active membership is required to join this event',
      };
    }
    return { ok: true };
  }

  return { ok: true };
}

export interface PlayGenderRestrictionInput {
  genderRestriction: PlayGenderRestriction;
  participantGender: PlayParticipantGender | null | undefined;
}

export type PlayGenderRestrictionErrorCode = 'GENDER_RESTRICTION';

export function validatePlayEventGenderRestriction(
  input: PlayGenderRestrictionInput
): { ok: true } | { ok: false; code: PlayGenderRestrictionErrorCode; message: string } {
  const { genderRestriction, participantGender } = input;

  if (genderRestriction === 'open') {
    return { ok: true };
  }

  if (participantGender !== genderRestriction) {
    const message =
      genderRestriction === 'male'
        ? 'This event is for men only'
        : 'This event is for women only';
    return { ok: false, code: 'GENDER_RESTRICTION', message };
  }

  return { ok: true };
}
